import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import Button from '@alltrails/shared/denali/components/Button';

import { Filters, FilterViewModel, FilterRangeUpdates, FilterSliderUpdates } from 'types/Search';
import { SearchResultsCountFunction } from 'hooks/useHypotheticalSearchResultsCount';
import CustomProvider from 'components/CustomProvider';
import ExploreFilterBar from 'components/explore/FilterBar';
import FiltersProvider from 'components/explore/FiltersProvider';

import { useSelector } from '../../redux/hooks';
import { clearAllFilters, clearRadioFilter, toggleRadioFilter, setFilter, rangeFilter, toggleFilter } from '../../redux/slices/filters';
import * as styles from './styles/styles.module.scss';

type FilterBarProps = {
  resultsCount: number;
  getFiltersConfigurations: (filters: Filters) => FilterViewModel[];
  searchResultsCountFunction: SearchResultsCountFunction;
};

const FilterBar = ({ resultsCount, getFiltersConfigurations, searchResultsCountFunction }: FilterBarProps): JSX.Element => {
  const dispatch = useDispatch();
  const authState = useSelector(state => state.authState);
  const defaultFiltersState = useSelector(state => state.filters.defaultFilters);
  const filtersState = useSelector(state => state.filters.filters);
  // This is necessary to prevent errors when the explore FilterBar tries changing filters for display purposes
  const filtersString = JSON.stringify(filtersState);
  const filters = JSON.parse(filtersString);
  const sortedFilters = filters ? getFiltersConfigurations(filters) : [];
  const enabledFilters = sortedFilters.map(filter => filter.enabledKey);

  const handleClearAllFilters = useCallback(() => {
    dispatch(clearAllFilters());
  }, [dispatch]);

  const handleClearRadioFilter = useCallback(
    (filterKey: string) => {
      dispatch(clearRadioFilter({ filterKey }));
    },
    [dispatch]
  );

  const handleFilterRadioToggle = useCallback(
    (filterKey: string, filterConfigKey: string) => {
      dispatch(toggleRadioFilter({ filterKey, filterConfigKey }));
    },
    [dispatch]
  );

  const handleFilterSet = useCallback(
    (filterKey: string, value: string) => {
      dispatch(setFilter({ filterKey, value }));
    },
    [dispatch]
  );

  const handleFilterRange = useCallback(
    (filterKey: string, value: FilterRangeUpdates | FilterSliderUpdates) => {
      dispatch(rangeFilter({ filterKey, value }));
    },
    [dispatch]
  );

  const handleFilterToggle = useCallback(
    (filterKey: string, filterConfigKey: string) => {
      dispatch(toggleFilter({ filterKey, filterConfigKey }));
    },
    [dispatch]
  );

  const showClearAllButton = filtersString !== JSON.stringify(defaultFiltersState);

  return (
    <div className={styles.wrapper} data-testid="filter-bar">
      <CustomProvider>
        <FiltersProvider
          clearAllFilters={handleClearAllFilters}
          clearRadioFilter={handleClearRadioFilter}
          handleFilterRadioToggle={handleFilterRadioToggle}
          handleFilterRange={handleFilterRange}
          handleFilterSet={handleFilterSet}
          handleFilterToggle={handleFilterToggle}
          page={null}
          resultsCount={resultsCount}
          searchResultsCountFunction={searchResultsCountFunction}
          sortedFilters={sortedFilters}
        >
          <ExploreFilterBar context={authState} enabledFilters={enabledFilters} />
        </FiltersProvider>
      </CustomProvider>
      {showClearAllButton && <Button text="Clear all" className={styles.button} onClick={handleClearAllFilters} testId="Clear all" variant="flat" />}
    </div>
  );
};

export default FilterBar;
